import { Link, useLocation } from "react-router-dom";
import {
  FileText,
  Activity,
  Settings,
  Users,
  Route,
  BookA,
  BookOpen,
  Search as SearchIcon,
  Home,
  PlayCircle,
  Key,
  Cable,
  FolderOpen,
  LifeBuoy,
  Brain,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { features } from "@/lib/features";
import { trpc } from "@/lib/trpc";
import { STALE_TIMES } from "@/lib/query-performance.js";
import { useOptionalProject } from "@/components/project-context";
import { IamuWordmark } from "@/components/brand/logo";
import { SetupProgress } from "@/components/setup-progress";
import { CanEdit, ViewOnlyChip } from "@/lib/project-access";
import { WorkspaceSwitcher } from "./workspace-switcher";
import { ProjectSwitcher } from "./project-switcher";
import { UserMenu } from "./user-menu";

export { MobileSidebar } from "./mobile-sidebar";

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: React.ReactNode;
  exact?: boolean;
};

export function NavContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const activeProject = useOptionalProject();

  const { data: queueCounts } = trpc.finding.queueCounts.useQuery(
    { projectId: activeProject?.id ?? "" },
    { enabled: !!activeProject, staleTime: STALE_TIMES.list },
  );
  const problemCount = queueCounts?.openProblems ?? 0;
  const triageCount = queueCounts?.views?.triage ?? 0;
  const fixNextCount = queueCounts?.views?.fix_next ?? 0;

  function renderNavLink(item: NavItem) {
    const [basePath, query] = item.to.split("?");
    const active = query
      ? location.pathname === basePath && location.search.includes(query)
      : item.exact
        ? location.pathname === basePath && !location.search
        : location.pathname.startsWith(basePath);
    const Icon = item.icon;
    return (
      <Link
        key={item.to}
        to={item.to}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        className={cn(
          "group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-base transition-all",
          active
            ? "active font-medium bg-sidebar-accent text-sidebar-foreground shadow-xs"
            : "text-sidebar-muted-foreground hover:bg-sidebar-accent/60 hover:text-sidebar-foreground",
        )}
      >
        {active && (
          <span className="absolute -left-0.5 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-sm bg-sidebar-primary" />
        )}
        <Icon
          className="h-4 w-4 shrink-0 text-sidebar-muted-foreground group-hover:text-sidebar-foreground group-[.active]:text-sidebar-primary transition-colors"
          aria-hidden="true"
        />
        {item.label}
        {item.badge}
      </Link>
    );
  }

  const knowledgeItems: NavItem[] = activeProject
    ? [
        ...(features.knowledgeSources
          ? [
              {
                to: `/projects/${activeProject.id}/sources`,
                label: "Sources",
                icon: FileText,
              },
            ]
          : []),
        ...(features.terminology
          ? [
              {
                to: `/projects/${activeProject.id}/terminology`,
                label: "Terminology",
                icon: BookA,
              },
            ]
          : []),
      ]
    : [];

  const projectSections: { label: string; items: NavItem[] }[] = activeProject
    ? [
        {
          label: "Work",
          items: [
            {
              to: `/projects/${activeProject.id}/scenarios`,
              label: "Scenarios",
              icon: Route,
            },
            {
              to: `/projects/${activeProject.id}/walkthroughs`,
              label: "Walkthroughs",
              icon: PlayCircle,
            },
            {
              to:
                fixNextCount === 0 && triageCount > 0
                  ? `/projects/${activeProject.id}/findings?view=triage`
                  : `/projects/${activeProject.id}/findings`,
              label: "Findings",
              icon: SearchIcon,
              badge:
                problemCount > 0 ? (
                  <span className="ml-auto rounded-full bg-sidebar-accent border border-sidebar-border px-1.5 py-0.5 text-xs font-semibold leading-none tabular-nums text-sidebar-foreground">
                    {problemCount}
                  </span>
                ) : undefined,
            },
            {
              to: `/projects/${activeProject.id}/personas`,
              label: "Personas",
              icon: Users,
            },
            {
              to: `/projects/${activeProject.id}/activity`,
              label: "Activity",
              icon: Activity,
            },
          ],
        },
        ...(knowledgeItems.length > 0
          ? [{ label: "Knowledge", items: knowledgeItems }]
          : []),
      ]
    : [
        {
          label: "Workspace",
          items: [
            {
              to: "/projects",
              label: "Projects",
              icon: FolderOpen,
              exact: true,
            },
            {
              to: "/settings",
              label: "Workspace settings",
              icon: Settings,
              exact: true,
            },
            {
              to: "/settings?tab=access-tokens",
              label: "Access tokens",
              icon: Key,
            },
            {
              to: "/settings?tab=integrations",
              label: "Integrations",
              icon: Cable,
            },
          ],
        },
      ];

  return (
    <nav
      className="flex flex-1 flex-col overflow-y-auto px-2.5 py-2"
      aria-label="Main navigation"
    >
      {activeProject && (
        <div className="pb-2 mb-1 space-y-0.5">
          {renderNavLink({
            to: `/projects/${activeProject.id}`,
            label: "Overview",
            icon: Home,
            exact: true,
          })}
          {renderNavLink({
            to: `/projects/${activeProject.id}/context`,
            label: "Context",
            icon: Brain,
          })}
        </div>
      )}
      {projectSections.length > 0 && (
        <div className="pt-2">
          {projectSections.map((section, i) => (
            <div
              key={section.label}
              className={cn("space-y-0.5", i > 0 && "mt-4 pt-1")}
            >
              {section.items.map(renderNavLink)}
            </div>
          ))}
        </div>
      )}
      <div className="flex-1" />
      {activeProject && (
        <CanEdit>
          <SetupProgress onNavigate={onNavigate} />
        </CanEdit>
      )}
      {activeProject && (
        <div className="px-2.5 pb-1 space-y-0.5">
          {renderNavLink({
            to: `/projects/${activeProject.id}/settings`,
            label: "Project settings",
            icon: Settings,
          })}
          {renderNavLink({
            to: "/settings",
            label: "Workspace settings",
            icon: Settings,
            exact: true,
          })}
        </div>
      )}
      <div className="px-2.5 pb-2">
        <a
          href="https://docs.iamu.dev"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-base text-sidebar-muted-foreground transition-all hover:bg-sidebar-accent/60 hover:text-sidebar-foreground"
        >
          <BookOpen
            className="h-4 w-4 shrink-0 text-sidebar-muted-foreground group-hover:text-sidebar-foreground"
            aria-hidden="true"
          />
          Docs
        </a>
      </div>
    </nav>
  );
}

export function SidebarFooter({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <div className="px-5 pb-1">
        <a
          href="mailto:support@iamu.dev"
          className="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-sidebar-muted-foreground transition-colors hover:text-sidebar-foreground"
        >
          <LifeBuoy className="h-4 w-4 shrink-0" aria-hidden="true" />
          Help & feedback
        </a>
      </div>
      <UserMenu onNavigate={onNavigate} />
    </>
  );
}

export function Sidebar() {
  return (
    <aside
      className="relative hidden w-[240px] flex-col overflow-hidden bg-sidebar md:flex"
      role="complementary"
      aria-label="Project sidebar"
    >
      <div className="flex h-full flex-col">
        <div className="shrink-0 px-4 pt-4 pb-1">
          <Link to="/projects" className="inline-flex" aria-label="Home">
            <IamuWordmark className="text-sidebar-foreground" />
          </Link>
        </div>
        <div className="shrink-0 px-3.5 py-2 space-y-1.5">
          <WorkspaceSwitcher />
          <ProjectSwitcher />
          <ViewOnlyChip className="mt-2 bg-sidebar-accent text-sidebar-foreground" />
        </div>
        <NavContent />
        <SidebarFooter />
      </div>
    </aside>
  );
}
